"use client";
import {useState, useCallback} from "react";
import styles from './index.module.scss';
import IconButton from "@/app/_components/Shared/IconButton";
import BusinessDetails from "@/app/_components/Steps/BusinessDetails";
import PersonalDetails from "@/app/_components/Steps/PersonalDetails";
import BusinessPreferences from "@/app/_components/Steps/BusinessPreferences";
import AdditionalServices from "@/app/_components/Steps/AdditionalServices";
import Timeline from "@/app/_components/Steps/Timeline";
import Complete from "@/app/_components/Steps/Complete";
import {useRouter} from "nextjs-toploader/app";
import axios from "axios";
import {toast} from "react-toastify";
import {object, string, array} from "yup";
import { parsePhoneNumberFromString } from 'libphonenumber-js';

const steps = [
    "Business Details",
    "Personal Details",
    "Business Preferences",
    "Additional Services",
    "Timeline and Investment",
    "Complete"
];

const stepsSchema = {
    1: {
        jurisdiction: string().required(),
    },
    2: {
        full_name: string().required(),
        email: string().email().required(),
        phone: string() .test('is-valid-international-phone', 'Invalid phone number', value => {
            if (!value) return false;
            const phone = parsePhoneNumberFromString(value);
            return phone ? phone.isValid() : false;
        }).required(),
        preferred_contact_method: string().required(),
    },
    3: {
        business_name: string(),
        primary_activity: string().required(),
        require_physical_office: string().required(),
        office_location: string(),
    },
    4: {
        additional_services: array(),
    },
    5: {
        timeline: string().required(),
    }
}
// merge all schemas into one
const schema = object().shape(Object.assign({}, ...Object.values(stepsSchema)));

export default function Steps() {
    const router = useRouter();
    const [isLoading, setIsLoading] = useState(false);
    const [step, setStep] = useState(1);
    const [data, setData] = useState({
        jurisdiction: 'Mainland',
        full_name: '',
        email: '',
        phone: '',
        preferred_contact_method: 'Email',
        has_a_name_in_mind: 'Yes',
        business_name: '',
        shareholder_number: '1',
        primary_activity: null,
        require_physical_office: 'Yes',
        office_location: '',
        additional_services: [],
        timeline: 'Immediately',
    });

    const handleNext = useCallback(() => {
        const schema = object().shape(stepsSchema[step]);
        schema.validate(data).then(() => {
            setStep(step + 1);
        }).catch(error => {
            toast.error(error.message);
        });
    }, [data, step]);

    const handleBack = useCallback(() => {
        setStep(step - 1);
    }, [step]);

    const handleChange = useCallback((key, value) => {
        setData({...data, [key]: value});
    }, [data]);

    const submit = useCallback(async () => {
        setIsLoading(true);
        schema.validate(data).then(() => {
            let message = 'Type: Business Setup\n';
            // Generate a message based on the data provided
            message += `Jurisdiction: ${data.jurisdiction}\n`;
            message += `Full Name: ${data.full_name}\n`;
            message += `Email: ${data.email}\n`;
            message += `Phone: ${data.phone}\n`;
            message += `Preferred Contact Method: ${data.preferred_contact_method}\n`;
            message += `Business Name: ${data.business_name}\n`;
            message += `Shareholder Number: ${data.shareholder_number}\n`;
            message += `Primary Activity: ${data.primary_activity}\n`;
            message += `Require Physical Office: ${data.require_physical_office}\n`;
            message += `Office Location: ${data.office_location}\n`;
            message += `Additional Services: ${data.additional_services.join(', ')}\n`;
            message += `Timeline: ${data.timeline}\n`;
            return axios.post( process.env.NEXT_PUBLIC_API_URL + '/tickets', {
                name: data.full_name,
                type: 'business_setup',
                email: data.email,
                phone: data.phone,
                message,
                data: data
            }).then(response => {
                handleNext();
            }).catch(error => {
                toast.error('Failed to send message. Please try again later');
            });
        }).catch(error => {
            toast.error(error.message);
        }).finally(() => {
            setIsLoading(false);
        });
    }, [data, handleNext])

    return (
        <div className="container-wrapper">
            <div className={styles.container}>
                <div className="container-wrapper">
                    <div className={styles.sidebar}>
                        <div className="container-wrapper">
                            {steps.map((stepName, index) => (
                                <div key={index} className={`${styles.step} ${step === index + 1 ? styles.active : ''}`}>
                                    <span>{index + 1}</span>
                                    {stepName}
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className={styles.form}>
                        <div className="container-wrapper">
                            {
                                step === 1 && <BusinessDetails data={data} onChange={handleChange} />
                            }
                            {
                                step === 2 && <PersonalDetails data={data} onChange={handleChange} />
                            }
                            {
                                step === 3 && <BusinessPreferences data={data} onChange={handleChange} />
                            }
                            {
                                step === 4 && <AdditionalServices data={data} onChange={handleChange} />
                            }
                            {
                                step === 5 && <Timeline data={data} onChange={handleChange} />
                            }
                            {
                                step === 6 && <Complete data={data} onChange={handleChange} />
                            }
                            <div className={styles.buttons}>
                                {
                                    (step > 1 && step < 6) && <IconButton color="secondary" onClick={handleBack}>Previous</IconButton>
                                }
                                {
                                    (step < 5) && <IconButton onClick={handleNext}>Next</IconButton>
                                }
                                {
                                    (step === 5) && <IconButton onClick={submit}>Complete</IconButton>
                                }
                                {
                                    (step === 6) && <IconButton onClick={() => router.push('/services/bank-account-opening/')}>Discover Services</IconButton>
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
